/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { Button, Card, Image, message, Upload, Modal } from "antd";
import passport from "../../../../assets/images/passport.png";
import self_pic from "../../../../assets/images/self_pic.png";
import axios from "axios";
import { X, RotateCw } from "lucide-react";

type UploadImageProps = {
  t: any;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
  passportData: any;
  setPassportData: (response: any) => void;
  faceMatchingData: any;
  setFaceMatchingData: (response: any) => void;
  passportImage: any;
  setPassportImage: (image: any) => void;
  selfieImage: any;
  setSelfieImage: (image: any) => void;
};

const UploadImage = ({
  t,
  loading,
  setLoading,
  error,
  setError,
  passportData,
  setPassportData,
  faceMatchingData,
  setFaceMatchingData,
  passportImage,
  setPassportImage,
  selfieImage,
  setSelfieImage,
}: UploadImageProps) => {
  const [loadingPassport, setLoadingPassport] = useState(false);
  const [loadingSelfie, setLoadingSelfie] = useState(false);
  const [errorPassport, setErrorPassport] = useState<string | null>(null);
  const [errorSelfie, setErrorSelfie] = useState<string | null>(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>(
    []
  );
  const [selectedCamera, setSelectedCamera] = useState<string>("");
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const mobileRegex =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      setIsMobile(mobileRegex.test(userAgent));
    };

    checkMobile();
  }, []);

  // Get available cameras
  const getAvailableCameras = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === "videoinput"
      );
      setAvailableCameras(videoDevices);

      // Set default camera (prefer front camera on mobile)
      const frontCamera = videoDevices.find(
        (device) =>
          device.label.toLowerCase().includes("front") ||
          device.label.toLowerCase().includes("user")
      );
      setSelectedCamera(
        frontCamera?.deviceId || videoDevices[0]?.deviceId || ""
      );
    } catch (err) {
      console.error("Error getting cameras:", err);
      message.error("Failed to get available cameras");
    }
  };

  // Start camera with selected device
  const startCamera = async () => {
    try {
      // Stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      const constraints = {
        video: {
          deviceId: selectedCamera ? { exact: selectedCamera } : undefined,
          facingMode: isMobile ? "user" : undefined,
          width: { ideal: isMobile ? 1280 : 1920 },
          height: { ideal: isMobile ? 720 : 1080 },
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch((err) => {
          console.error("Error playing video:", err);
          message.error("Failed to start video stream");
        });
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      message.error("Failed to access camera. Please check your permissions.");
    }
  };

  // Effect to initialize camera when modal opens
  useEffect(() => {
    if (isCameraOpen) {
      getAvailableCameras().then(() => startCamera());
    }
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [isCameraOpen]);

  // Effect to restart camera when selected device changes
  useEffect(() => {
    if (isCameraOpen && selectedCamera) {
      startCamera();
    }
  }, [selectedCamera]);

  // Switch camera function
  const switchCamera = async () => {
    if (availableCameras.length <= 1) return;

    const currentIndex = availableCameras.findIndex(
      (camera) => camera.deviceId === selectedCamera
    );
    const nextIndex = (currentIndex + 1) % availableCameras.length;
    setSelectedCamera(availableCameras[nextIndex].deviceId);
  };

  // Function to capture photo
  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    const context = canvas.getContext("2d");
    if (context) {
      // If using front camera on mobile, flip the image horizontally
      if (isMobile) {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob
      canvas.toBlob(
        async (blob) => {
          if (blob) {
            const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });
            await handleSelfieUpload(file);
            stopCamera();
          }
        },
        "image/jpeg",
        0.9
      );
    }
  };

  // Stop camera function
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraOpen(false);
  };

  // Function to handle selfie upload
  const handleSelfieUpload = async (file: File) => {
    setLoadingSelfie(true);
    try {
      const formData = new FormData();

      if (passportImage) {
        formData.append("img1", passportImage);
      }

      formData.append("img2", file);

      // Best-effort face matching (fallback: keep selfie even if API fails)
      try {
        const result = await axios.post(
          "https://demo.computervision.com.vn/api/v3/ekyc/face_matching?type1=card&format_type=file",
          formData,
          {
            auth: {
              username: process.env.REACT_APP_API_KEY || "",
              password: process.env.REACT_APP_API_SECRET || "",
            },
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        setFaceMatchingData(result.data.data);
      } catch (faceMatchingErr) {
        console.error(
          "Error uploading selfie (face matching):",
          faceMatchingErr
        );
        setFaceMatchingData(null);
        message.warning(
          t?.("face_matching.unavailable", { ns: "message" }) ||
            "Face matching is temporarily unavailable. You can continue without it."
        );
      }

      setSelfieImage(file);
      setErrorSelfie(null);
      message.success("Selfie uploaded successfully");
    } catch (err) {
      console.error("Error uploading selfie:", err);
      // Fallback: keep the selfie locally so user can continue.
      setSelfieImage(file);
      setFaceMatchingData(null);
      setErrorSelfie(null);
      message.warning(
        t?.("face_matching.unavailable", { ns: "message" }) ||
          "Face matching is temporarily unavailable. You can continue without it."
      );
    } finally {
      setLoadingSelfie(false);
    }
  };

  // Upload props for passport
  const UploadPassportProps = {
    name: "imgPassport",
    customRequest: async (options: any) => {
      const { file, onSuccess, onError } = options;
      setLoadingPassport(true);
      try {
        // Best-effort OCR: if it fails, user can still continue and fill data manually.
        try {
          const formData = new FormData();
          formData.append("img", file);

          const result = await axios.post(
            "https://demo.computervision.com.vn/api/v3/ekyc/card?format_type=file&get_thumb=true",
            formData,
            {
              auth: {
                username: process.env.REACT_APP_API_KEY || "",
                password: process.env.REACT_APP_API_SECRET || "",
              },
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          const info = result?.data?.data?.info;
          setPassportData(info || null);
        } catch (ocrErr) {
          console.error("Error performing passport OCR:", ocrErr);
          setPassportData(null);
          message.warning(
            t?.("registration_form.step1.ocr_unavailable") ||
              "Passport OCR is temporarily unavailable. Please fill in passport information manually."
          );
        }

        setPassportImage(file);
        setErrorPassport(null);
        onSuccess("Uploaded successfully");
        message.success(`${file.name} file uploaded successfully`);

        // Check if selfie image already exists, if so call face matching API
        if (selfieImage) {
          try {
            const faceMatchingFormData = new FormData();
            faceMatchingFormData.append("img1", file); // New passport image
            faceMatchingFormData.append("img2", selfieImage); // Existing selfie image

            const faceMatchingResult = await axios.post(
              "https://demo.computervision.com.vn/api/v3/ekyc/face_matching?type1=card&format_type=file",
              faceMatchingFormData,
              {
                auth: {
                  username: process.env.REACT_APP_API_KEY || "",
                  password: process.env.REACT_APP_API_SECRET || "",
                },
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );

            setFaceMatchingData(faceMatchingResult.data.data);
            message.success("Face matching completed successfully");
          } catch (faceMatchingErr) {
            console.error("Error performing face matching:", faceMatchingErr);
            setFaceMatchingData(null);
            message.warning(
              t?.("face_matching.unavailable", { ns: "message" }) ||
                "Face matching is temporarily unavailable. You can continue without it."
            );
          }
        }
      } catch (err) {
        console.error("Error uploading passport:", err);
        setErrorPassport("Failed to upload the passport.");
        onError(err);
        message.error(`${file.name} file upload failed.`);
      } finally {
        setLoadingPassport(false);
      }
    },
  };
  // Upload props for selfie
  const UploadSelfieProps = {
    name: "imgSelfie",
    customRequest: async (options: any) => {
      const { file, onSuccess, onError } = options;
      setLoadingSelfie(true);
      try {
        const formData = new FormData();
        // Fetch passport image as a blob and append it
        // const response = await fetch(passportImage);
        // const blob = await response.blob();
        // const passportFile = new File([blob], "passport.jpg", {
        //   type: blob.type
        // });

        formData.append("img1", passportImage); // Append the passport image as a file
        formData.append("img2", file); // Append the selfie image

        // Best-effort face matching (fallback: keep selfie even if API fails)
        try {
          const result = await axios.post(
            "https://demo.computervision.com.vn/api/v3/ekyc/face_matching?type1=card&format_type=file",
            formData,
            {
              auth: {
                username: process.env.REACT_APP_API_KEY || "",
                password: process.env.REACT_APP_API_SECRET || "",
              },
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          setFaceMatchingData(result.data.data);
        } catch (faceMatchingErr) {
          console.error(
            "Error uploading selfie (face matching):",
            faceMatchingErr
          );
          setFaceMatchingData(null);
          message.warning(
            t?.("face_matching.unavailable", { ns: "message" }) ||
              "Face matching is temporarily unavailable. You can continue without it."
          );
        }

        setSelfieImage(file); // Set the selfie image
        setErrorSelfie(null);
        onSuccess("Uploaded successfully");
        message.success(`${file.name} file uploaded successfully`);
      } catch (err) {
        console.error("Error uploading selfie:", err);
        // Fallback: keep the selfie locally so user can continue.
        setSelfieImage(file);
        setFaceMatchingData(null);
        setErrorSelfie(null);
        onSuccess("Uploaded successfully");
        message.warning(
          t?.("face_matching.unavailable", { ns: "message" }) ||
            "Face matching is temporarily unavailable. You can continue without it."
        );
      } finally {
        setLoadingSelfie(false);
      }
    },
  };
  // const handleOpenCamera = () => {
  //   setIsCameraOpen(true);
  // };

  return (
    <>
      <div className="w-full py-4 px-8 bg-primary-lightblue rounded-lg text-primary-blue text-base font-semibold">
        {t("registration_form.step1.rule")}{" "}
        <span className="text-red-500">*</span>
      </div>

      <div
        id="upload-images"
        className="flex flex-col lg:flex-row gap-6 w-full"
      >
        {/* Passport Card */}
        <Card title={t("registration_form.step1.passport")} className="flex-1">
          <div className="flex flex-col gap-4 items-center">
            <Image
              src={
                passportImage ? URL.createObjectURL(passportImage) : passport
              }
              alt="Passport"
              className="min-w-[300px] min-h-[250px]"
              preview={false}
            />
            <Upload
              {...UploadPassportProps}
              accept="image/*"
              beforeUpload={(file) => {
                const isImage = file.type.startsWith("image/");
                if (!isImage) {
                  message.error("You can only upload image files!");
                }
                return isImage || Upload.LIST_IGNORE;
              }}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />} loading={loadingPassport}>
                {t("cta.select_file")}
              </Button>
            </Upload>
          </div>
        </Card>

        {/* Selfie Card */}
        <Card title={t("registration_form.step1.selfie")} className="flex-1">
          <div className="flex flex-col items-center gap-4">
            <Image
              src={selfieImage ? URL.createObjectURL(selfieImage) : self_pic}
              alt="Selfie"
              className="max-w-[300px]"
              preview={false}
            />
            {/* <Button
              icon={<Camera className="w-4 h-4" />}
              loading={loadingSelfie}
              onClick={handleOpenCamera}
              className="hidden md:flex"
            >
              {t("cta.take_photo")}
            </Button> */}
            <Upload
              {...UploadSelfieProps}
              accept="image/*"
              beforeUpload={(file) => {
                const isImage = file.type.startsWith("image/");
                if (!isImage) {
                  message.error("You can only upload image files!");
                }
                return isImage || Upload.LIST_IGNORE;
              }}
              showUploadList={false}
            >
              <Button
                icon={<UploadOutlined />}
                loading={loadingSelfie}
                disabled={passportImage === null}
              >
                {t("cta.select_file")}
              </Button>
            </Upload>
          </div>
        </Card>
      </div>

      {/* Camera Modal */}
      <Modal
        title="Take a Selfie"
        open={isCameraOpen}
        onCancel={stopCamera}
        footer={[
          <Button key="cancel" onClick={stopCamera}>
            Cancel
          </Button>,
          availableCameras.length > 1 && (
            <Button
              key="switch"
              icon={<RotateCw className="w-4 h-4" />}
              onClick={switchCamera}
            >
              Switch Camera
            </Button>
          ),
          <Button key="capture" type="primary" onClick={capturePhoto}>
            Capture
          </Button>,
        ].filter(Boolean)}
        width={640}
      >
        <div className="flex flex-col items-center">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full max-w-[600px] mb-4"
            style={{
              transform: isMobile ? "scaleX(-1)" : "none",
            }}
          />
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </div>
      </Modal>

      {/* Error message */}
      {error && (
        <div className="w-full py-2 px-8 bg-red-100 rounded-lg text-red-500 text-sm flex justify-between items-center">
          {error}
          <X
            size={16}
            className="cursor-pointer"
            onClick={() => setError(null)}
          />
        </div>
      )}
    </>
  );
};

export default UploadImage;
